import { useQuery } from '@tanstack/react-query';
import { History, LayoutTemplate, ListOrdered, Server as ServerIcon, SlidersHorizontal } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Disclosure, Panel, Pill, SectionHeader, Skeleton } from '@/components/surface/ui';
import { AUTOMATION_CATALOG_KEY, RUNS_KEY, automationApi } from '@/lib/automation-api';
import { getErrorMessage } from '@/lib/errors';
import { SubscriptionNotice } from '@/modules/billing/SubscriptionNotice';
import { LaunchPanel } from '@/modules/automation/LaunchPanel';
import { ParameterFields } from '@/modules/automation/ParameterFields';
import { SAFE_STRATEGY, changedValues, initialSelection, initialValues, isCompleteSelection, parameterErrors } from '@/modules/automation/parameters';
import { RecentRuns } from '@/modules/automation/RecentRuns';
import { TargetPicker } from '@/modules/automation/TargetPicker';
import { PageHeader, TechnicalDetails } from '@/modules/automation/ui';
import type { ParameterValue, RunMode, RunStrategy, TargetPreview, TargetSelection, TemplateSummary } from '@/types/api';

export function TemplateRunPage() {
  const { key = '' } = useParams();
  const catalog = useQuery({ queryKey: AUTOMATION_CATALOG_KEY, queryFn: () => automationApi.catalog(), staleTime: 60_000 });
  const template = catalog.data?.templates.find((item) => item.key === key);

  if (catalog.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24" />
        <Skeleton className="h-72" />
      </div>
    );
  }

  if (catalog.isError) {
    return <p className="text-sm text-danger">The template could not be loaded: {getErrorMessage(catalog.error)}</p>;
  }

  if (template === undefined) {
    return (
      <div className="space-y-4">
        <PageHeader title="Template not found" back={{ to: '/automation/templates', label: 'Templates' }} />
        <p className="text-sm text-muted-foreground">There is no template called “{key}”.</p>
      </div>
    );
  }

  return <TemplateForm key={template.key} template={template} />;
}

function TemplateForm({ template }: { template: TemplateSummary }) {
  const [values, setValues] = useState<Record<string, ParameterValue>>(() => initialValues(template.parameters));
  const [searchParams] = useSearchParams();
  const [selection, setSelection] = useState<TargetSelection>(() => initialSelection(searchParams));
  const [strategy, setStrategy] = useState<RunStrategy>(SAFE_STRATEGY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [preview, setPreview] = useState<TargetPreview | null>(null);

  const onPreview = useCallback((next: TargetPreview | null) => setPreview(next), []);
  const serverCount = preview !== null && preview.problems.length === 0 ? preview.servers.length : null;

  const blockedReason = useMemo(() => {
    if (!isCompleteSelection(selection)) return 'Choose the servers to run on.';
    if (preview !== null && preview.problems.length > 0) return preview.problems[0] ?? null;
    return null;
  }, [selection, preview]);

  const build = (mode: RunMode) => ({
    kind: 'template' as const,
    subject: template.key,
    parameters: changedValues(template.parameters, values),
    mode,
    strategy,
    targets: selection,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        back={{ to: '/automation/templates', label: 'Templates' }}
        eyebrow={
          <>
            <Pill tone="primary" icon={LayoutTemplate}>
              Template
            </Pill>
            <Pill tone="muted">Version {template.version}</Pill>
          </>
        }
        title={template.name}
        description={template.summary}
        actions={
          <Link
            to={`/help/templates/${template.key}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium transition-colors hover:bg-muted/40"
          >
            <LayoutTemplate className="size-4" aria-hidden />
            Guide and options
          </Link>
        }
      />

      <SubscriptionNotice />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-6">
          <Panel>
            <SectionHeader icon={ListOrdered} title="What it does" />
            <div className="space-y-4 px-5 pb-5">
              <p className="text-sm leading-6">{template.description}</p>
              <dl className="text-[13px]">
                <dt className="text-muted-foreground">Supported systems</dt>
                <dd>{template.supported_systems}</dd>
              </dl>
              <ol className="space-y-2">
                {template.steps.map((step, index) => (
                  <li key={`${step.playbook}-${index}`} className="flex items-start gap-3 text-[13px]">
                    <span className="inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-primary/[0.1] text-xs font-semibold text-primary">
                      {index + 1}
                    </span>
                    <span className="pt-0.5">
                      <Link to={`/automation/playbooks/${step.playbook}`} className="font-medium hover:text-primary hover:underline">
                        {step.label}
                      </Link>
                      {step.optional && <span className="text-muted-foreground"> (only when chosen below)</span>}
                    </span>
                  </li>
                ))}
              </ol>
              <p className="text-xs text-muted-foreground">
                Steps run in order on each server, over one connection. When a step fails, the steps after it are skipped on
                that server.
              </p>
            </div>
            <TechnicalDetails details={template.details} />
            {template.changes.length > 0 && (
              <Disclosure title="Version history" meta={`${template.changes.length} version${template.changes.length === 1 ? '' : 's'}`}>
                <ul className="space-y-1.5 px-5 text-[13px]">
                  {template.changes.map((change) => (
                    <li key={change.version}>
                      <span className="font-medium">{change.version}</span>: {change.notes}
                    </li>
                  ))}
                </ul>
              </Disclosure>
            )}
          </Panel>

          {template.parameters.length > 0 && (
            <Panel>
              <SectionHeader icon={SlidersHorizontal} title="Choices" />
              <div className="px-5 pb-5">
                <ParameterFields
                  parameters={template.parameters}
                  values={values}
                  onChange={(parameter, value) => setValues((current) => ({ ...current, [parameter]: value }))}
                  errors={parameterErrors(errors)}
                />
              </div>
            </Panel>
          )}

          <Panel>
            <SectionHeader icon={ServerIcon} title="Servers" description="Each step's playbook checks it supports each server before the run changes anything." />
            <div className="px-5 pb-5">
              <TargetPicker
                value={selection}
                onChange={setSelection}
                kind="template"
                subject={template.key}
                error={errors['targets.values'] ?? errors['targets.mode']}
                onPreview={onPreview}
              />
            </div>
          </Panel>
        </div>

        <div className="space-y-6 xl:sticky xl:top-20">
          <LaunchPanel
            strategy={strategy}
            onStrategyChange={setStrategy}
            serverCount={serverCount}
            allowDryRun
            build={build}
            onFieldErrors={setErrors}
            blockedReason={blockedReason}
          />

          <Panel>
            <SectionHeader
              icon={History}
              title="Recent runs"
              actions={
                <Link to={`/automation/runs?kind=template&subject=${template.key}`} className="text-xs font-medium text-primary hover:underline">
                  All
                </Link>
              }
            />
            <RecentRuns queryKey={[...RUNS_KEY, 'template', template.key]} filters={{ kind: 'template', subject: template.key, limit: 5 }} />
          </Panel>
        </div>
      </div>
    </div>
  );
}
